import React, { useState } from 'react';
import './SpacesModal.css';

function SpacesModal({ closeModal, addQuoteItem, formatPrice }) {
  const [name, setName] = useState('');
  const [hotStorage, setHotStorage] = useState(''); 
  const [hotDataTransfer, setHotDataTransfer] = useState('');
  const [coldStorage, setColdStorage] = useState('');
  const [coldDataTransfer, setColdDataTransfer] = useState(''); 


  // Included storage and transfer
  const includedStorage = 250;
  const includedDataTransfer = 1024;

  // Conversion factor from GiB to GB
  const conversionFactorGiBtoGB = 1.0737;

  // Conversion factor from TiB to TB
  const conversionFactorTiBtoTB = 1.0995;

  // Conversion factor from PiB to PB
  const conversionFactorPiBtoPB = 1.1259;

  // Pricing rates
  const hotStorageRate = 0.02; // $ per GiB
  const coldStorageRate = 0.007; // $ per GiB
  const hotTransferRate = 0.01; // $ per GiB
  const coldTransferRate = 0.01; // $ per GiB

  // Calculate costs 
  const HotStorage = parseInt(hotStorage, 10) || 0; // GiBs of storage
  const HotDataTransfer = parseInt(hotDataTransfer, 10) || 0; // GiBs of transfer
  const ColdStorage = parseInt(coldStorage, 10) || 0; // GiBs of storage
  const ColdDataTransfer = parseInt(coldDataTransfer, 10) || 0; // GiBs of transfer

  // PRICING LOGIC:
  // 
  // Storage quota (250 GiB): Hot storage uses quota first, remaining applies to cold
  // Examples:
  //   - 200 GiB hot + 100 GiB cold: hot uses 200 GiB quota, cold uses 50 GiB quota, 50 GiB cold charged
  //   - 300 GiB hot + 100 GiB cold: hot uses all 250 GiB quota (50 GiB charged), all 100 GiB cold charged
  //
  // Transfer quota (1,024 GiB): Hot transfer uses quota first, remaining applies to cold
  // Cold transfer is FREE up to cold storage amount
  // Examples:
  //   - 100 GiB cold storage + 150 GiB cold transfer, 50 GiB remaining quota: 
  //     50 GiB uses quota (free), 100 GiB matches cold storage (free), 0 GiB charged
  //   - 100 GiB cold storage + 200 GiB cold transfer, 0 GiB remaining quota: 
  //     100 GiB matches cold storage (free), 100 GiB charged

  // Storage quota logic: Hot storage gets precedence using the included quota
  // First, apply included quota to hot storage
  const hotStorageAfterQuota = Math.max(0, HotStorage - includedStorage);
  const remainingStorageQuota = Math.max(0, includedStorage - HotStorage);
  
  // Then apply remaining quota (if any) to cold storage
  const coldStorageAfterQuota = Math.max(0, ColdStorage - remainingStorageQuota);
  
  // Calculate costs
  const hotStorageCost = hotStorageAfterQuota * hotStorageRate;
  const coldStorageCost = coldStorageAfterQuota * coldStorageRate;

  // Transfer quota logic: Hot transfer gets precedence using the included quota
  // First, apply included quota to hot transfer
  const hotTransferAfterQuota = Math.max(0, HotDataTransfer - includedDataTransfer);
  const remainingTransferQuota = Math.max(0, includedDataTransfer - HotDataTransfer);
  
  // Then apply remaining quota (if any) to cold transfer
  const coldTransferAfterQuota = Math.max(0, ColdDataTransfer - remainingTransferQuota);
  
  // Calculate costs
  const hotDataTransferCost = hotTransferAfterQuota * hotTransferRate;
  
  // Cold transfer is free up to the amount of cold storage
  const chargableColdTransfer = Math.max(0, coldTransferAfterQuota - ColdStorage);
  const coldDataTransferCost = chargableColdTransfer * coldTransferRate;

  // For display purposes
  const excessHotStorage = hotStorageAfterQuota;
  const excessColdStorage = ColdStorage;
  const excessHotDataTransfer = hotTransferAfterQuota;
  const excessColdDataTransfer = ColdDataTransfer;

  const basePrice = 5; // Base price 
  const totalPrice = basePrice + hotStorageCost + coldStorageCost + hotDataTransferCost + coldDataTransferCost;

  const handleAddSpaces = () => {
    // Smart display logic accounting for quota sharing between hot and cold
    const totalStorageUsed = HotStorage + ColdStorage;
    const totalTransferUsed = HotDataTransfer + ColdDataTransfer;
    
    let formattedStorage, formattedTransfer;
    
    // If total usage is within quota, just show the quota (no labels needed)
    if (totalStorageUsed <= includedStorage) {
      formattedStorage = `${includedStorage.toLocaleString()} GiB (${(includedStorage * conversionFactorGiBtoGB).toLocaleString(undefined, {maximumFractionDigits: 0})} GB) storage`;
    } else {
      // Exceeds quota - show hot and cold separately with labels
      const hotStorageStr = HotStorage > 0 ? `${HotStorage.toLocaleString()} GiB (${(HotStorage * conversionFactorGiBtoGB).toLocaleString(undefined, {maximumFractionDigits: 0})} GB) standard storage` : '';
      const coldStorageStr = ColdStorage > 0 ? `${ColdStorage.toLocaleString()} GiB (${(ColdStorage * conversionFactorGiBtoGB).toLocaleString(undefined, {maximumFractionDigits: 0})} GB) cold storage` : '';
      formattedStorage = [hotStorageStr, coldStorageStr].filter(Boolean).join('; ');
    }
    
    // If total usage is within quota, just show the quota (no labels needed)
    if (totalTransferUsed <= includedDataTransfer) {
      formattedTransfer = `${includedDataTransfer.toLocaleString()} GiB (${(includedDataTransfer * conversionFactorGiBtoGB).toLocaleString(undefined, {maximumFractionDigits: 0})} GB) transfer`;
    } else {
      // Exceeds quota - show hot and cold separately with labels
      const hotTransferStr = HotDataTransfer > 0 ? `${HotDataTransfer.toLocaleString()} GiB (${(HotDataTransfer * conversionFactorGiBtoGB).toLocaleString(undefined, {maximumFractionDigits: 0})} GB) standard transfer` : '';
      const coldTransferStr = ColdDataTransfer > 0 ? `${ColdDataTransfer.toLocaleString()} GiB (${(ColdDataTransfer * conversionFactorGiBtoGB).toLocaleString(undefined, {maximumFractionDigits: 0})} GB) cold transfer` : '';
      formattedTransfer = [hotTransferStr, coldTransferStr].filter(Boolean).join('; ');
    }
      
    addQuoteItem({
      serviceType: 'Spaces',
      name: name,
      description: `Object storage with:`,
      features: `${formattedStorage}; ${formattedTransfer}.`,
      priceMonthly: formatPrice(totalPrice),
      quantity: 1,
      totalPrice: formatPrice(totalPrice)
    });
    closeModal();
  };


  return (
    <div id="modal" className="modal spaces-modal">
      <div id="modal-content" className="modal-content">
        <span id="close" className="close" onClick={closeModal}>&times;</span>
        <div id="modal-header" className="modal-header flex flex-between flex-center">
          <div>
            <h1>
              Add Spaces
            </h1>
          </div>
          <div id="modal-item-name" className="modal-item-name">
            <input
              type="text"
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter item name or description"
              maxLength="50"
            />
          </div>
        </div>
        <div id="modal-body" className="modal-body">
          <div id="alert-info" className="flex flex-center">
            <svg className="alert-info" aria-hidden="true">
              <use href="#_sprite-alert-info"></use>
            </svg> 
            <div className="period font-blueGray font-weight-400">           
              Spaces cost $5 and include 250 GiB of storage and 1,024 GiB of transfer per month. Hot storage has precedence for quota usage.<br />
            </div>
          </div>
          <div id="alert-info" className="flex flex-center">
            <svg className="alert-info" aria-hidden="true">
              <use href="#_sprite-alert-info"></use>
            </svg> 
            <div className="period font-blueGray font-weight-400">           
             Cold transfer is free up to cold storage amount.
            </div>
          </div>






          <h5>SELECT STANDARD AND COLD STORAGE</h5>
          
          <div id="storage-container" className="flex gap-2">
            <div id="calculation-container" className="calculation-container">
              <div id="calculation-container-price" className="calculation-container-price flex">
                <div className="price">
                  <span className="dollar dollar-large">$</span>
                  <span className="cost cost-large">{hotStorageCost ? formatPrice(hotStorageCost) : '0'}</span>
                  <span className="period period-large">/mo</span>
                </div>
                <div>
                  <span>
                    {includedStorage + excessHotStorage > 1024 * 1024
                      ? `${((includedStorage + excessHotStorage) / 1024 / 1024).toFixed(2).replace(/\.0+$/, '')} PiB (${((includedStorage + excessHotStorage) / 1024 / 1024 * conversionFactorPiBtoPB).toFixed(2).replace(/\.0+$/, '')} PB) storage`
                      : includedStorage + excessHotStorage > 1024
                        ? `${((includedStorage + excessHotStorage) / 1024).toFixed(2).replace(/\.0+$/, '')} TiB (${((includedStorage + excessHotStorage) / 1024 * conversionFactorTiBtoTB).toFixed(2).replace(/\.0+$/, '')} TB) storage`
                        : `${(includedStorage + excessHotStorage)} GiB (${(((includedStorage + excessHotStorage) * conversionFactorGiBtoGB).toFixed(0))} GB) storage`}
                  </span>
                </div>
              </div>
              <div id="calculation-container-input" className="calculation-container-input">
                <input 
                  type="text" 
                  spellCheck="false" 
                  required 
                  placeholder="Enter standard storage"  
                  value={hotStorage}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d*$/.test(value)) { // Only allow digits
                      const numValue = parseInt(value, 10);
                      if (!isNaN(numValue) && numValue >= 0 && numValue <= 104857600) {
                        setHotStorage(value);
                      } else if (value === '') {
                        setHotStorage('');
                      }
                    }
                  }}
                />
                <div id="floatlabel-price" className="floatlabel-price">
                  <label>Enter standard storage in GiB</label>
                </div>
              </div>
            </div>
            <div id="calculation-container" className="calculation-container">
              <div id="calculation-container-price" className="calculation-container-price flex">
                <div className="price">
                  <span className="dollar dollar-large">$</span>
                  <span className="cost cost-large">{coldStorageCost ? formatPrice(coldStorageCost) : '0'}</span>
                  <span className="period period-large">/mo</span>
                </div>
                <div>
                  <span>
                    {excessColdStorage > 1024 * 1024
                      ? `${(excessColdStorage / 1024 / 1024).toFixed(2).replace(/\.0+$/, '')} PiB (${(excessColdStorage / 1024 / 1024 * conversionFactorPiBtoPB).toFixed(2).replace(/\.0+$/, '')} PB) storage`
                      : excessColdStorage > 1024
                        ? `${(excessColdStorage / 1024).toFixed(2).replace(/\.0+$/, '')} TiB (${(excessColdStorage / 1024 * conversionFactorTiBtoTB).toFixed(2).replace(/\.0+$/, '')} TB) storage`
                        : `${excessColdStorage} GiB (${((excessColdStorage * conversionFactorGiBtoGB).toFixed(0))} GB) storage`}
                  </span>
                </div>
              </div>
              <div id="calculation-container-input" className="calculation-container-input">
                <input 
                  type="text" 
                  spellCheck="false" 
                  required 
                  placeholder="Enter cold storage"  
                  value={coldStorage}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d*$/.test(value)) { // Only allow digits
                      const numValue = parseInt(value, 10);
                      if (!isNaN(numValue) && numValue >= 0 && numValue <= 104857600) {
                        setColdStorage(value);
                      } else if (value === '') {
                        setColdStorage('');
                      }
                    }
                  }}
                />
                <div id="floatlabel-price" className="floatlabel-price">
                  <label>Enter cold storage in GiB</label>
                </div>
              </div>
            </div>

          </div>
          <h5>SELECT STANDARD AND COLD TRANSFER</h5>
          <div id="transfer-container" className="flex gap-2">
            <div id="calculation-container" className="calculation-container">
              <div id="calculation-container-price" className="calculation-container-price flex">
                <div className="price">
                  <span className="dollar dollar-large">$</span>
                  <span className="cost cost-large">{hotDataTransferCost ? formatPrice(hotDataTransferCost) : '0'}</span>
                  <span className="period period-large">/mo</span>
                </div>
                <div>
                  <span>
                    {includedDataTransfer + excessHotDataTransfer > 1024 * 1024
                      ? `${((includedDataTransfer + excessHotDataTransfer) / 1024 / 1024).toFixed(2).replace(/\.0+$/, '')} PiB (${((includedDataTransfer + excessHotDataTransfer) / 1024 / 1024 * conversionFactorPiBtoPB).toFixed(2).replace(/\.0+$/, '')} PB) transfer`
                        : includedDataTransfer + excessHotDataTransfer > 1024
                        ? `${((includedDataTransfer + excessHotDataTransfer) / 1024).toFixed(2).replace(/\.0+$/, '')} TiB (${((includedDataTransfer + excessHotDataTransfer) / 1024 * conversionFactorTiBtoTB).toFixed(2).replace(/\.0+$/, '')} TB) transfer`
                        : `${(includedDataTransfer + excessHotDataTransfer)} GiB (${((includedDataTransfer + excessHotDataTransfer) * conversionFactorGiBtoGB).toFixed(0)} GB) transfer`}
                  </span>
                </div>
              </div>
              <div id="calculation-container-input" className="calculation-container-input">
                <input 
                  type="text" 
                  spellCheck="false" 
                  required 
                  placeholder="Enter standard transfer per month"  
                  value={hotDataTransfer}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d*$/.test(value)) { // Only allow digits
                      const numValue = parseInt(value, 10);
                      if (!isNaN(numValue) && numValue >= 0 && numValue <= 104857600) {
                        setHotDataTransfer(value);
                      } else if (value === '') {
                        setHotDataTransfer('');
                      }
                    }
                  }}
                />
                <div id="floatlabel-price" className="floatlabel-price">
                  <label>Enter standard transfer in GiB</label>
                </div>
              </div>
            </div>
            <div id="calculation-container" className="calculation-container">
              <div id="calculation-container-price" className="calculation-container-price flex">
                <div className="price">
                  <span className="dollar dollar-large">$</span>
                  <span className="cost cost-large">{coldDataTransferCost ? formatPrice(coldDataTransferCost) : '0'}</span>
                  <span className="period period-large">/mo</span>
                </div>
                <div>
                  <span>
                    {excessColdDataTransfer > 1024 * 1024
                      ? `${(excessColdDataTransfer / 1024 / 1024).toFixed(2).replace(/\.0+$/, '')} PiB (${(excessColdDataTransfer / 1024 / 1024 * conversionFactorPiBtoPB).toFixed(2).replace(/\.0+$/, '')} PB) transfer`
                        : excessColdDataTransfer > 1024
                        ? `${(excessColdDataTransfer / 1024).toFixed(2).replace(/\.0+$/, '')} TiB (${(excessColdDataTransfer / 1024 * conversionFactorTiBtoTB).toFixed(2).replace(/\.0+$/, '')} TB) transfer`
                        : `${excessColdDataTransfer} GiB (${(excessColdDataTransfer * conversionFactorGiBtoGB).toFixed(0)} GB) transfer`}
                  </span>
                </div>
              </div>
              <div id="calculation-container-input" className="calculation-container-input">
                <input 
                  type="text" 
                  spellCheck="false" 
                  required 
                  placeholder="Enter cold transfer per month"  
                  value={coldDataTransfer}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d*$/.test(value)) { // Only allow digits
                      const numValue = parseInt(value, 10);
                      if (!isNaN(numValue) && numValue >= 0 && numValue <= 104857600) {
                        setColdDataTransfer(value);
                      } else if (value === '') {
                        setColdDataTransfer('');
                      }
                    }
                  }}
                />
                <div id="floatlabel-price" className="floatlabel-price">
                  <label>Enter cold transfer in GiB</label>
                </div>
              </div>
            </div>

          </div>
        </div>
        <div id="modal-footer" className="modal-footer flex flex-between flex-center">
          <div id="action-button" className="action-button">
            <button
              className="button-blue"
              onClick={handleAddSpaces}
            >
              <span>Add Spaces</span>
            </button>
          </div>
          <div id="price" className="price">
            <span className="dollar dollar-large">$</span>
            <span className="cost cost-large">{formatPrice((basePrice + hotStorageCost + coldStorageCost + hotDataTransferCost + coldDataTransferCost))}</span>
            <span className="period period-large">/mo</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SpacesModal;